import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from 'vue'
import { useRouter } from 'vue-router/composables'

import { useCareerAiSend } from '@/composables/ai/useCareerAiSend'
import { useCareerAiServiceMenu } from '@/composables/ai/useCareerAiServiceMenu'
import {
  createTrackEvent,
  extractLinkHref
} from '@/composables/ai/useCareerAiTracking'
import careerAIClient from '@/services/career-ai-client'
import { useCareerAiHandoverStore } from '@/stores/careerAiHandover'
import careerChatMenu from '@/utils/ai/careerChatMenu.json'
import { INTERVIEW_FIRST_QUESTION } from '@/utils/ai/interviewStyles'

import CareerAiHeader from './CareerAiHeader.vue'
import CareerAiInputArea from './CareerAiInputArea.vue'
import CareerAiMessageList from './CareerAiMessageList.vue'

/* eslint-disable @typescript-eslint/no-explicit-any */

// モードの正本は careerChatMenu.json（AIキャリア相談画面と共用）

export default /*#__PURE__*/_defineComponent({
  __name: 'CareerAI',
  props: {
  uuid: { type: String, default: '' },
  sessionId: { type: String, default: '' },
  initialMode: { type: String, default: 'concierge' },
  remaining: { type: Number, default: 30 },
  dailyLimit: { type: Number, default: 30 },
  isPrime: { type: Boolean, default: false },
  hideHeader: { type: Boolean, default: false }, // 親側でヘッダーを描画する場合
  // 入力欄の＋メニュー（他のAI機能への切替）。AIダッシュボード側には
  // 専用の切替UIがあるため、出すのはマイページのボットだけ
  showServiceMenu: { type: Boolean, default: false },
  // 引き継いだ会話。空なら挨拶から始める（マイページ→全画面の受け渡し）
  initialMessages: { type: Array as () => any[], default: () => [] },
  // 引き継いだ求人カードの実体。メッセージは jobIds しか持たない
  initialJobsCache: { type: Object, default: () => ({}) },
  // 引き継いだ未送信のボット発言のモード（全画面へ移ったとき）
  initialKickoffMode: { type: String, default: '' },
  // 引き継いだ「明示的に選んだ機能」のモード
  initialLockedMode: { type: String, default: '' }
},
  emits: ['close', 'clear', 'mode-change'],
  setup(__props, { expose, emit }) {

const props = __props;

const VALID_MODES = Object.keys(careerChatMenu.modeConfig)

// 全画面のAIキャリア相談。routes/aiRoutes.ts と対応
const AI_CAREER_PATH = '/ai/career'

// サーバが返す remaining がこの値以上なら無制限ユーザー扱い
const UNLIMITED_REMAINING = 9000

// モードに切り替えたときにボットが先に出す1問目。
//
// サーバーは履歴を career_ai_chat_log から作るので、画面に積むだけでは AI に
// 見えない。次の送信で `assistant_prefix` として渡し、履歴へ入れてもらう
const MODE_KICKOFF: Record<string, string> = {
  interview: INTERVIEW_FIRST_QUESTION,
  ...((careerChatMenu as { modeKickoff?: Record<string, string> })
    .modeKickoff || {})
}

const INITIAL_GREETING =
  'お手伝いできることはありますかメェ～\n該当するものがあれば選択してください。\nなければチャットで話しかけてメェ！'

const buildInitialMessage = (): any => ({
  role: 'assistant',
  text: INITIAL_GREETING,
  jobIds: [],
  suggestMode: null,
  modeSwitchDismissed: false
})




const router = useRouter()
// 全画面へ会話を引き継ぐ。session_id を渡せばサーバー側の文脈もそのまま続く
const handover = useCareerAiHandoverStore()
// $globalVar は Vue.prototype 由来のアプリ横断フラグ。composable 化までの経由地
const globalVar = getCurrentInstance()?.proxy?.$globalVar as any

const messageList = ref<{ scrollToBottom: () => void } | null>(null)
const inputArea = ref<{ focus: () => void } | null>(null)

const initialModeOrDefault = (): string =>
  VALID_MODES.indexOf(props.initialMode) >= 0 ? props.initialMode : 'concierge'

const input = ref('')
const loading = ref(false)
const isComposing = ref(false)
const currentMode = ref(initialModeOrDefault())
const sessionRemaining = ref(props.remaining)
const rateLimitIsPrime = ref<boolean | null>(null)
const jobsCache = ref<Record<string, any>>({
  ...(props.initialJobsCache || {})
})
const feedbackGiven = ref<Record<string, string>>({})
const messages = ref<any[]>(
  props.initialMessages.length
    ? props.initialMessages.slice()
    : [buildInitialMessage()]
)
// ＋メニューでユーザーが明示的に選んだモード。空＝未指定（コンシェルジュ）。
// 入っている間はサーバー応答でモードを変えない（面接の途中で問い合わせ対応に
// 流れてしまうのを防ぐ）
const lockedMode = ref(props.initialLockedMode || '')
// 画面が先に出したボット発言のモード。次の送信でサーバーへ渡し、
// サーバー側の本文で履歴に入れてもらう（本文は渡さない）
const kickoffMode = ref(props.initialKickoffMode || '')

// 引き継ぐときは chatEnd を投げない。遷移先が同じ session_id を使うため、
// onBeforeUnmount の chatEnd が生きているセッションを終わらせてしまう
const sessionClosed = ref(false)

// 文言は careerChatMenu.json。全画面のAIキャリア相談では
// ログイン方法・バックアップ等のサポート質問は出さない（相談に集中させる）
const quickReplies = computed(() =>
  props.hideHeader
    ? careerChatMenu.careerQuickReplies
    : careerChatMenu.quickReplies
)

const isUnlimitedUser = computed(
  () => sessionRemaining.value >= UNLIMITED_REMAINING
)

// 429 で届いた会員種別を最優先。429 を経ず残数 0 になる経路があるため prop も見る。
// prop を渡さない画面（マイページ）でプライム会員に加入導線を出さないよう、
// 最後はアプリ全体のプライム判定（is_subscribed 由来）へフォールバックする
const isPrimeUser = computed(() => {
  if (rateLimitIsPrime.value !== null) return rateLimitIsPrime.value
  if (props.isPrime === true) return true
  return !!(globalVar && globalVar.isYagiPrime)
})

const inputPlaceholder = computed(() =>
  !isUnlimitedUser.value && sessionRemaining.value <= 0
    ? '本日の上限に達しました'
    : 'ヤギコンシェルジュに質問する'
)

// 最新のAI回答に対してフィードバックチップを出す対象index（出さないなら -1）
// 「解決しましたか？」はお問い合わせ(inquiry)モードのみ。キャリア相談等の対話では出さない
const feedbackTargetIndex = computed(() => {
  if (loading.value) return -1
  if (currentMode.value !== 'inquiry') return -1
  const idx = messages.value.length - 1
  if (idx <= 0) return -1 // 初回greeting(0)には出さない
  const last = messages.value[idx]
  if (
    !last ||
    last.role !== 'assistant' ||
    !last.text ||
    last.isPlaceholder ||
    last.isServiceConfirm
  )
    return -1
  return idx
})

// 吹き出し一覧は子が持つので、スクロールも子へ委譲する
const scrollToBottom = (): void => {
  nextTick(() => messageList.value?.scrollToBottom())
}

const trackEvent = createTrackEvent(
  () => props.uuid,
  () => props.sessionId
)

const { fetchJobsDetails, sendMessage } = useCareerAiSend({
  getUuid: () => props.uuid,
  getSessionId: () => props.sessionId,
  messages,
  jobsCache,
  currentMode,
  input,
  isComposing,
  lockedMode,
  kickoffMode,
  loading,
  sessionRemaining,
  rateLimitIsPrime,
  validModes: VALID_MODES,
  scrollToBottom,
  trackEvent
})

onMounted(() => {
  if (!props.initialMessages.length) return
  // 引き継いだ会話は最新まで送る（既定は scrollTop=0 で最古の挨拶が出る）
  scrollToBottom()
  // 求人カードの取得が終わる前に遷移した場合、キャッシュが欠けたまま届く。
  // メッセージが参照している id のうち手元に無いものを取り直す
  const missing = props.initialMessages
    .flatMap((msg: any) => msg.jobIds || [])
    .filter((id: string) => !jobsCache.value[id])
  if (missing.length) fetchJobsDetails(missing)
})

onBeforeUnmount(() => {
  if (props.uuid && !sessionClosed.value) {
    careerAIClient.chatEnd(props.uuid).catch(() => {})
  }
})

// 親が新セッションを発行した（履歴クリア後 etc）→ 内部状態を初期化
watch(
  () => props.sessionId,
  (newVal, oldVal) => {
    if (!oldVal || !newVal || newVal === oldVal) return
    messages.value = [buildInitialMessage()]
    feedbackGiven.value = {}
    // 古い選択先の確認チップを新セッションに持ち込まない
    resetServiceState()
    lockedMode.value = ''
    kickoffMode.value = ''
    currentMode.value = initialModeOrDefault()
    input.value = ''
    loading.value = false
    // v-show 保持のため再マウントされない。chat/start が返した最新残数に同期する
    sessionRemaining.value = props.remaining
  }
)

// モードはセッション中に変わるため、親が描画する場合に固定されないよう通知する
watch(currentMode, (newVal) => emit('mode-change', newVal))

const confirmClear = (): void => {
  if (loading.value) return
  if (typeof window === 'undefined') return
  if (
    !window.confirm(
      '新しいチャットを始めますか？\nこれまでのやり取りは消去されます。'
    )
  )
    return
  emit('clear')
}

const prefill = (text: string): void => {
  input.value = text
  // 入力欄は子が持つ。フォーカスは公開された最小 API 経由で戻す
  nextTick(() => inputArea.value?.focus())
}

const handleApply = (job: any): void => {
  if (!job || !job.id || job.disabled) return
  trackEvent('career_ai_job_card_click', { job_id: job.id })
  // yagidia 求人詳細ページに遷移（新規タブ）
  // 同一ドメインの /yagidia/:id で求人詳細が開ける
  window.open(`/yagidia/${job.id}`, '_blank', 'noopener')
}

const onBubbleLinkClick = (e: any): void => {
  const href = extractLinkHref(e)
  if (href) trackEvent('career_ai_link_click', { url: href })
}

// 「わかった」/「わからない」フィードバック。index 単位で確定し再表示を防ぐ。
// 新しいキーの追加は差し替えで行う（$set を増やさない）
const onFeedback = (index: number, value: string): void => {
  feedbackGiven.value = { ...feedbackGiven.value, [index]: value }
  trackEvent('career_ai_answer_feedback', { value, mode: currentMode.value })
}

const onContactClick = (): void =>
  trackEvent('career_ai_contact_click', { mode: currentMode.value })

// 全画面（AIキャリア相談）へ移動する。マイページのボットからの導線
const openFullScreen = (): void => {
  trackEvent('career_ai_expand_click', {})
  if (props.sessionId) {
    sessionClosed.value = true
    handover.stash({
      uuid: props.uuid,
      sessionId: props.sessionId,
      mode: currentMode.value,
      messages: messages.value,
      jobsCache: jobsCache.value,
      // 1問目に答える前に全画面へ移ることがある。未送信だと遷移先で
      // 文脈が抜けるので一緒に運ぶ
      kickoffMode: kickoffMode.value,
      lockedMode: lockedMode.value,
      target: AI_CAREER_PATH
    })
  }
  router.push(AI_CAREER_PATH)
}

// 上限到達バナーからのヤギプライム導線。加入率の測定用
const trackPrimeCta = (): void =>
  trackEvent('career_ai_prime_cta_click', { daily_limit: props.dailyLimit })

// ＋メニュー。文言・遷移先の正本は careerChatMenu.json
const serviceMenuItems = careerChatMenu.serviceMenu || []
const serviceMenuConfirm: any = careerChatMenu.serviceMenuConfirm || {}

const {
  pendingService,
  resetServiceState,
  onServiceMenuSelect,
  confirmServiceMove,
  stayInServiceMode,
  cancelServiceMove
} = useCareerAiServiceMenu({
  messages,
  currentMode,
  lockedMode,
  kickoffMode,
  loading,
  validModes: VALID_MODES,
  modeKickoff: MODE_KICKOFF,
  confirmLabels: serviceMenuConfirm,
  // 全画面の案内はマイページのボットだけ（全画面側で出すと二重になる）
  nudgeEnabled: () => props.showServiceMenu,
  navigate: (route: string) => router.push(route),
  stashConversation: (mode: string, route: string, handedMessages: any[]) =>
    handover.stash({
      uuid: props.uuid,
      sessionId: props.sessionId,
      mode,
      messages: handedMessages,
      jobsCache: jobsCache.value,
      kickoffMode: kickoffMode.value,
      lockedMode: lockedMode.value,
      target: route
    }),
  discardConversation: () => handover.take(props.uuid),
  onMoved: () => emit('close'),
  scrollToBottom,
  trackEvent
})

// 全画面のAIキャリア相談（CareerChat.vue）が相談メニューから呼ぶ。
// <script setup> は既定で何も公開しないため明示する
expose({ prefill })

return { __sfc: true,VALID_MODES, AI_CAREER_PATH, UNLIMITED_REMAINING, MODE_KICKOFF, INITIAL_GREETING, buildInitialMessage, props, emit, router, handover, globalVar, messageList, inputArea, initialModeOrDefault, input, loading, isComposing, currentMode, sessionRemaining, rateLimitIsPrime, jobsCache, feedbackGiven, messages, lockedMode, kickoffMode, sessionClosed, quickReplies, isUnlimitedUser, isPrimeUser, inputPlaceholder, feedbackTargetIndex, scrollToBottom, trackEvent, fetchJobsDetails, sendMessage, confirmClear, prefill, handleApply, onBubbleLinkClick, onFeedback, onContactClick, openFullScreen, trackPrimeCta, serviceMenuItems, serviceMenuConfirm, pendingService, resetServiceState, onServiceMenuSelect, confirmServiceMove, stayInServiceMode, cancelServiceMove, CareerAiHeader, CareerAiInputArea, CareerAiMessageList }
}

})